import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { formatLocationName } from '../../core/util/location';
import PhoneLoginForm from '../../components/PhoneLoginForm/PhoneLoginForm';
import OtpCodeLoginForm from '../../components/OtpCodeLoginForm/OtpCodeLoginForm';
import { analyticsTrackEvent } from '../../core/analytics';
import { LOGIN_PAGE_LOAD } from '../../core/analytics/events';
import { getCurrentPath } from '../../core/util/url';
import { isEmptyObject, safeGet } from '../../core/util/object';
import ClinicLogo from '../Location/ClinicLogo';
import { isEmptyArray } from '../../core/util/array';
import BrandedHeader from './BrandedHeader';
import { emptyFunction } from '../../core/util/function';
import history from '../../core/history';
import * as entryPoints from './entryPoints';

const title = 'Login';

const Root = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  max-width: 330px;
  margin: 30px auto 0;
  padding-bottom: 40px;
  text-align: left;
`;

const ClinicLogoContainer = styled(ClinicLogo)`
  margin: 36px auto;
  text-align: center;
  & img {
    height: auto;
    max-width: 300px;
    max-height: 120px;
  }
`;

class LoginForm extends Component {
  componentDidMount = () => {
    const {
      invoiceId,
      fetchLocationDataFromInvoice,
      locationId,
      groupId,
      saveNewInvoiceSource,
      groupLocations,
      fetchGroupLocations,
    } = this.props;
    analyticsTrackEvent(LOGIN_PAGE_LOAD, {
      loginPath: getCurrentPath(),
    });

    if (invoiceId) {
      fetchLocationDataFromInvoice(invoiceId);
    }

    if (locationId || groupId) {
      saveNewInvoiceSource({ locationId, groupId });
    }

    if (groupId && isEmptyObject(groupLocations)) {
      fetchGroupLocations(groupId);
    }
  };

  getHeader = () => {
    const { isModal, login } = this.props;
    if (this.shouldShowClinicLogo()) {
      const fixedLocation = this.fixPUCCFWAllianceLogoInLocation();
      return (
        <ClinicLogoContainer location={fixedLocation} alt={formatLocationName(fixedLocation)} />
      );
    } else if (isModal) {
      return null;
    }
    return <BrandedHeader closeModal={this.handleCloseModal} loginAction={login.action} />;
  };

  getSubtitleText = () => {
    const ep = safeGet(this.props)('query.ep');
    //Entry Points
    switch (ep) {
      case entryPoints.CHECKLIST:
        return 'Manage your upcoming visit and save your information for next time.';
      case entryPoints.WAITLIST:
        return 'Complete your Solv account to save your information for next time.';
      case entryPoints.CONFIRMATION_EMAIL:
        return 'Update your info now to save time at the clinic.';
      case entryPoints.POST_VISIT_SUMMARY:
        return 'See a summary of your last visit and update your account information.';
      case entryPoints.THREE_DAY_CHECK_IN:
        return 'Finish setting up your Solv account to see your customized health checklist and your updated insurance info.';
      case entryPoints.CHECKLIST_REMINDER:
        return 'See your updated health checklist and easily book a visit.';
      case entryPoints.MONTHLY_INSURANCE_UPDATE_EMAIL:
        return 'Check out how you’re tracking toward your deductible and out-of-pocket max.';
      default:
        return 'Your Solv account saves your info to help you book appointments and manage visits with just a tap.';
    }
  };

  // Todo: FIX ME LATER
  // Alliance clinic (JA68Mg) changed from PUCCFW to UK4Ks
  // But we're *only* sending bills for old bookings (PUCCFW) so the logo needs
  // to look like the old ownership not the new.
  fixPUCCFWAllianceLogoInLocation = () => {
    const { invoiceId, location, groupLocations } = this.props;
    if (invoiceId && !isEmptyObject(location) && location.id === 'JA68Mg') {
      return {
        ...location,
        imageLinks: {
          ...location.imageLinks,
          logo: [
            // jscs:disable maximumLineLength
            'https://d3hmu1js3tz3r1.cloudfront.net/p/JA68Mg/Pediatric-Urgent-Care-of-Fort-Worth-Fort-Worth_20170922001746_logo.png',
          ],
        },
      };
    }

    if (!isEmptyObject(groupLocations) && !isEmptyArray(groupLocations.locations)) {
      return groupLocations.locations[0];
    }

    return location;
  };

  handleCloseModal = () => {
    const { isModal, hideLoginModal } = this.props;
    if (!isModal) {
      return history.push('/');
    }

    return hideLoginModal();
  };

  shouldShowClinicLogo = () => {
    const { isBookingWidgetEmbedded, groupId, invoiceId } = this.props;
    return !isBookingWidgetEmbedded && (invoiceId || groupId);
  };

  hasOTPBeenSent = () => this.props.login.login && this.props.login.login.otp_sent;

  render() {
    this.context.setTitle(title);
    return (
      <Fragment>
        {this.getHeader()}
        <Root>
          {this.hasOTPBeenSent() ? (
            <OtpCodeLoginForm {...this.props} />
          ) : (
            <PhoneLoginForm {...this.props} />
          )}
        </Root>
      </Fragment>
    );
  }
}

LoginForm.contextTypes = {
  setTitle: PropTypes.func.isRequired,
};

LoginForm.defaultProps = {
  onLoginSuccess: emptyFunction,
};

LoginForm.propTypes = {
  bookingId: PropTypes.string,
  login: PropTypes.object,
  onLoginSuccess: PropTypes.func,
  isBookingWidgetEmbedded: PropTypes.bool,
  invoiceId: PropTypes.string,
  fetchLocationDataFromInvoice: PropTypes.func,
  location: PropTypes.object.isRequired,
  locationId: PropTypes.string,
  groupId: PropTypes.string,
  saveNewInvoiceSource: PropTypes.func,
  groupLocations: PropTypes.object,
  fetchGroupLocations: PropTypes.func,
  shouldCreateAccountIfNotFound: PropTypes.bool,
  redirectToUrl: PropTypes.string,
  isLoginModalOpen: PropTypes.bool,
  query: PropTypes.object,
  isModal: PropTypes.bool,
  hideLoginModal: PropTypes.func,
};

export default LoginForm;
